import React, { createContext, useState, useEffect, useContext } from 'react';
import supabase from './supabase';
import { networkCountry, countryName } from './visitorCountry';

/**
 * Fills users.country from where the visitor is, when it is still empty.
 *
 * The app asks for it at profile setup; the website only offered a free-text
 * field on a skippable step, and the RSVP popup and Google sign-ups never
 * reach that step - so web signups were almost all blank in Office. Only
 * Cloudflare's reading is used (never a guess), and only an empty country is
 * filled: whatever someone typed or picked stays.
 */
const fillCountry = async (userId) => {
  const iso2 = await networkCountry();
  const name = iso2 && iso2 !== 'XX' && iso2 !== 'T1' ? countryName(iso2) : null;
  if (!name) return;
  await supabase.from('users').update({ country: name }).eq('id', userId).is('country', null);
};

const AuthContext = createContext({});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // The same person keeps the same `user` object. Supabase re-announces the
    // session whenever the tab or window regains focus (it re-checks the token
    // then), each time with a NEW user object - and every page that loads its
    // data in an effect on [user] (home, create, notifications, profile…)
    // took that as a different person and reloaded: the "page refreshes when
    // I come back to the tab". Replaced only when it really is someone else,
    // or their account details changed.
    const adopt = (next, event) => setUser((prev) => {
      if (!next) return null;
      if (prev && prev.id === next.id && event !== 'USER_UPDATED') return prev;
      return next;
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      adopt(session?.user ?? null);
      setLoading(false);
    });

    // Once per page load per account: the office dashboard's platform split.
    let recordedFor = null;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      adopt(session?.user ?? null, event);
      setLoading(false);
      if (session?.user?.id && recordedFor !== session.user.id) {
        recordedFor = session.user.id;
        supabase.rpc('record_user_platform', { p_platform: 'web' }).then(() => {}, () => {});

        // Their timezone, so a birthday reminder lands on the right local
        // date. Without it the reminder job has to assume UTC, which is a day
        // out at both ends of the world — the wrong day in Auckland and the
        // wrong day in Honolulu.
        //
        // Needs no permission and no prompt: the browser already knows, and
        // it is written on every sign-in so it follows anyone who moves.
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          if (tz) {
            supabase.from('users').update({ timezone: tz }).eq('id', session.user.id)
              .then(() => {}, () => {});
          }
        } catch {
          // An engine without Intl is not a reason to fail a sign-in.
        }

        fillCountry(session.user.id).catch(() => {});
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  const value = {
    user,
    loading,
    signOut: () => supabase.auth.signOut(),
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};